import { CircleAlert } from "lucide-react";
import type { MouseEventHandler } from "react";
import * as React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { useAnnounce } from "@/hooks/use-announcer";

/** Long enough to read the confirmation and react to it (WCAG 2.2.1). */
const CONFIRM_TIMEOUT_MS = 10_000;

/**
 * A two-click confirm. The armed state used to be conveyed only by a colour
 * change, or for icon buttons by a force-opened tooltip — neither of which a
 * screen reader reports, so the first click appeared to do nothing.
 */
export function ConfirmationButton(props: ButtonProps) {
  const [confirming, setConfirming] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const announce = useAnnounce();
  const isIcon =
    typeof props.size === "string" && props.size.startsWith("icon");
  const baseLabel = props["aria-label"];

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const startTimer = () => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setConfirming(false);
      // Silently disarming would leave a screen-reader user pressing a button
      // they believe is still armed.
      announce("Confirmation cancelled.");
    }, CONFIRM_TIMEOUT_MS);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cleanup only on unmount
  React.useEffect(() => clearTimer, []);

  const onClick: MouseEventHandler<HTMLButtonElement> = (ev) => {
    ev.stopPropagation();
    if (confirming) {
      clearTimer();
      props.onClick?.(ev as any);
      setConfirming(false);
    } else {
      setConfirming(true);
      startTimer();
      announce(
        baseLabel
          ? `${baseLabel}: press again to confirm.`
          : "Press again to confirm.",
        { politeness: "assertive" },
      );
    }
  };

  // Built separately so the spread keeps satisfying Button's size/name pairing:
  // overriding aria-label inline could narrow it to undefined.
  const confirmLabel =
    confirming && baseLabel ? { "aria-label": `Confirm: ${baseLabel}` } : {};

  return (
    <Button
      {...props}
      {...confirmLabel}
      onClick={onClick}
      onBlur={() => {
        clearTimer();
        setConfirming(false);
      }}
      variant={confirming ? "outline" : props.variant}
      className={
        confirming
          ? "border-warning-border text-warning-text hover:bg-warning-surface"
          : props.className
      }
    >
      {!confirming && props.children}
      {confirming && isIcon && (
        <>
          <CircleAlert className="h-4 w-4" aria-hidden="true" />
          {!baseLabel && (
            <span className="sr-only">Press again to confirm</span>
          )}
        </>
      )}
      {confirming && !isIcon && "Are you sure?"}
    </Button>
  );
}
